/**
 * Communication Systems (Division 27 — 27 01 00) shared configuration.
 *
 * SINGLE SOURCE OF TRUTH for Status values used across ALL Communications
 * Division screens (create modals, edit modals, tables, detail views, forms,
 * and frontend validation). Do NOT hardcode status lists elsewhere — import
 * from here so the value set and ordering stay consistent (DRY).
 *
 * Backend mirror: COMMUNICATION_STATUS_VALUES in backend/server.py
 */

import React from 'react';

// Ordered status value set (order is intentional and must be preserved).
export const COMMUNICATION_STATUS_VALUES = [
  'Draft',
  'In Progress',
  'Submitted',
  'Approved',
  'Installed',
  'Tested',
  'Completed',
  'Rejected',
];

// Ready-to-use option list for Radix <Select> / dropdowns ({ value, label }).
export const COMMUNICATION_STATUS_OPTIONS = COMMUNICATION_STATUS_VALUES.map((v) => ({
  value: v,
  label: v,
}));

// Default status for newly created Communications records.
export const DEFAULT_COMMUNICATION_STATUS = 'Draft';

// Submittal Data Type options for Communications (mirrors the Electrical set).
export const COMMUNICATION_DATA_TYPES = ['O&M', 'Warranty', 'Datasheet'];

/**
 * Normalize a stored status so existing/legacy records are never lost.
 * If a record's status is not part of the standard set, it is returned as-is
 * (so it still displays) rather than being dropped or overwritten.
 */
export const normalizeCommunicationStatus = (status) => {
  if (!status) return DEFAULT_COMMUNICATION_STATUS;
  return COMMUNICATION_STATUS_VALUES.includes(status) ? status : status;
};

// Centralized status → color mapping (single source for all Communications badges).
export const COMMUNICATION_STATUS_COLORS = {
  'Draft': 'bg-slate-100 text-slate-600 border-slate-200',
  'In Progress': 'bg-blue-50 text-blue-700 border-blue-200',
  'Submitted': 'bg-indigo-50 text-indigo-700 border-indigo-200',
  'Approved': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'Installed': 'bg-teal-50 text-teal-700 border-teal-200',
  'Tested': 'bg-cyan-50 text-cyan-700 border-cyan-200',
  'Completed': 'bg-green-50 text-green-700 border-green-200',
  'Rejected': 'bg-red-50 text-red-700 border-red-200',
};

// Reusable status pill for any Communications table/detail view.
export const CommStatusBadge = ({ status }) => {
  if (!status) return <span className="text-slate-300">—</span>;
  const cls = COMMUNICATION_STATUS_COLORS[status] || 'bg-slate-100 text-slate-600 border-slate-200';
  return <span className={`text-[11px] px-2 py-0.5 rounded-full border ${cls}`}>{status}</span>;
};

